import { useState } from 'react';
import { Button, ErrorState, Input } from '@helios/ui';
import { useAdminTenants } from '@/hooks/useAdmin';
import { SettingsField as Field } from './settingsChrome';
import { selectClassName } from './settingsPanels';

export function TenantPicker({
  value,
  onChange,
  label,
  allOption,
  fallback,
}: {
  value: string;
  onChange: (id: string) => void;
  label: string;
  allOption?: string;
  fallback?: { id: string; name: string };
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const query = useAdminTenants({ page, pageSize: 25, q: q.trim() || undefined });
  const items = query.data?.items ?? [];
  const selected = items.find((tenant) => tenant.id === value);
  const [retained, setRetained] = useState(fallback);
  const choose = (id: string) => {
    const tenant = items.find((item) => item.id === id);
    if (tenant) setRetained({ id: tenant.id, name: tenant.name });
    onChange(id);
  };
  return (
    <div className="grid gap-1.5">
      <Field label={`${label} search`}>
        <Input
          value={q}
          placeholder="Search workspaces"
          onChange={(event) => {
            setQ(event.target.value);
            setPage(1);
          }}
        />
      </Field>
      <Field label={label}>
        <select
          className={selectClassName}
          value={value}
          disabled={query.isLoading || query.isError}
          onChange={(event) => choose(event.target.value)}
        >
          {allOption ? (
            <option value="">{allOption}</option>
          ) : (
            <option value="" disabled>
              Select a workspace
            </option>
          )}
          {value && !selected ? (
            <option value={value}>{retained?.id === value ? retained.name : value}</option>
          ) : null}
          {items.map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name} ({tenant.status.toLowerCase()})
            </option>
          ))}
        </select>
      </Field>
      {query.isError ? (
        <QueryError
          title="Workspaces unavailable"
          error={query.error}
          retry={() => void query.refetch()}
        />
      ) : (
        <Pagination
          page={page}
          totalPages={query.data?.meta.totalPages ?? 0}
          onChange={setPage}
          label={label}
          compact
        />
      )}
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  onChange,
  label,
  compact = false,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  label: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 ${compact ? 'text-2xs' : 'text-xs'} text-muted-foreground`}
    >
      <span>
        {label}: page {page}
        {totalPages > 0 ? ` of ${totalPages}` : ''}
      </span>
      <div className="flex gap-1.5">
        <Button
          type="button"
          size="xs"
          variant="outline"
          aria-label={`${label} previous page`}
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          type="button"
          size="xs"
          variant="outline"
          aria-label={`${label} next page`}
          disabled={totalPages <= page}
          onClick={() => onChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
export function QueryError({
  title,
  error,
  retry,
}: {
  title: string;
  error: unknown;
  retry: () => void;
}) {
  return (
    <ErrorState
      title={title}
      description={error instanceof Error ? error.message : 'The action could not be completed.'}
      action={
        <Button type="button" size="xs" variant="outline" onClick={retry}>
          Retry
        </Button>
      }
    />
  );
}
